import { useEffect } from "react";
import { getEmbedAuth, setEmbedAuth } from "./auth";
import {
  refetchOnCredentialChange,
  type ErrorKind,
  type ViewerError,
} from "./client";

// Viewer <-> embedding-host postMessage protocol.
//
//  - The viewer never receives its credential in the URL. On load it announces
//    `ready` to its framing host (bounded retry) and the host replies with a
//    credential — an auth token (`authToken`, the go-forward path) and/or a
//    legacy API key (`apiKey`) — via postMessage, optionally alongside its own
//    component version (`hostVersion`) for analytics and version-specific
//    debugging.
//  - Once the load settles, the viewer reports its terminal state back to the
//    host — `loaded`, or `error` with the coarse ErrorKind plus, when cortex
//    answered, the raw HTTP `status` (never response bodies, URLs, or status
//    text) — so the host can log, retry, or degrade its own UI instead of
//    staring at an opaque frame. The status is forwarded uninterpreted: what
//    a 403 or 404 means is cortex's contract with its consumers, documented
//    once there, not re-encoded in the viewer or the embedding component.
//  - Every outbound message (`ready`, `loaded`, `error`) is posted with an
//    exact `targetOrigin` — never `"*"` — and only to a validated, allowlisted
//    host origin (fail-closed when unknown).
//  - The inbound `auth` reply must come from an allowlisted origin AND from
//    `window.parent` (`event.source` pinning), so a forged-origin or
//    same-origin-different-frame message can't inject a credential.

export const EMBED_MESSAGE_SOURCE = "aiera-embed";

interface ReadyMessage {
  source: typeof EMBED_MESSAGE_SOURCE;
  type: "ready";
}

interface AuthMessage {
  source: typeof EMBED_MESSAGE_SOURCE;
  type: "auth";
  /**
   * Legacy API-key credential. Being phased out in favor of `authToken` —
   * hosts must send at least one of the two.
   */
  apiKey?: string;
  /**
   * Auth-token credential, forwarded to cortex as `Authorization: Bearer`.
   * Preferred over `apiKey` when both are sent; will eventually be the only
   * accepted credential.
   */
  authToken?: string;
  /**
   * Optional self-reported version of the embedding host component, by
   * convention `name/semver` (the web component sends
   * "aiera-content/<package version>"). Normalized by
   * `normalizeHostVersion` before it reaches the auth store.
   */
  hostVersion?: string;
}

/**
 * Viewer -> host status report, sent when the load settles: the document
 * rendered, or the shell landed in one of its terminal error states. Additive
 * public API — hosts (the `<aiera-content>` web component) switch on `type`,
 * so new message types must not repurpose existing fields.
 */
export type ViewerStatusMessage =
  | { source: typeof EMBED_MESSAGE_SOURCE; type: "loaded" }
  | {
      source: typeof EMBED_MESSAGE_SOURCE;
      type: "error";
      kind: ErrorKind;
      /** Cortex's raw HTTP status; present only for `request-failed`. */
      status?: number;
    };

// Origins that may embed the viewer and deliver a credential, from per-environment
// config (env.ts validates the format). The same value is pasted into the CSP
// frame-ancestors list at deploy time, so the two layers can't drift: the CSP
// blocks the framing; this gates the handshake.
const CONFIGURED_PARENT_ORIGINS = (
  (import.meta.env.VITE_ALLOWED_PARENT_ORIGINS as string | undefined) ?? ""
)
  .trim()
  .split(/\s+/)
  .filter(Boolean);

// Local dev parents (the in-repo dev-host, the web-components demo host — see
// https://github.com/aiera-inc/web-components — or any localhost host page).
// Compiled out of production builds via the import.meta.env.DEV default.
const DEV_HOST_HOSTNAMES = ["localhost", "127.0.0.1"];

export interface HandshakeOptions {
  /** Test/dev seams — default to production behavior and the env config. */
  allowLocalhost?: boolean;
  allowedParentOrigins?: string[];
  hostOrigin?: string;
  parentWindow?: Window;
}

/**
 * Match one allowlist entry: `https://*.host` wildcards match subdomains by
 * suffix (not the apex, and only on the default port — mirroring how CSP
 * matches the same entry in frame-ancestors; an apex is allowed via its own
 * exact entry), while every other entry must equal the browser-asserted
 * origin exactly.
 */
function matchesEntry(entry: string, url: URL): boolean {
  if (entry.startsWith("https://*.")) {
    const suffix = entry.slice("https://*".length); // ".vendor.com"
    return url.hostname.endsWith(suffix) && url.port === "";
  }
  return url.origin === entry;
}

/**
 * Validate a framing-host origin against the configured allowlist. This is a
 * structural check on the parsed URL — not a substring test — so lookalikes
 * such as `https://aiera.com.evil.example` or `https://evil-aiera.com` are
 * rejected.
 */
export function isAllowedHostOrigin(
  origin: string,
  {
    allowLocalhost = import.meta.env.DEV,
    allowedParentOrigins = CONFIGURED_PARENT_ORIGINS,
  }: HandshakeOptions = {},
): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  if (allowLocalhost && DEV_HOST_HOSTNAMES.includes(url.hostname)) {
    return url.protocol === "http:" || url.protocol === "https:";
  }
  if (url.protocol !== "https:") return false;
  return allowedParentOrigins.some((entry) => matchesEntry(entry, url));
}

// Where status messages go. Set only from validated origins — the allowlisted
// framing-host origin at handshake start, then the browser-asserted origin of
// each valid `auth` — and null otherwise (top-level load, unresolvable or
// non-allowlisted parent), in which case status messages are silently dropped:
// the same fail-closed posture as `ready`.
let statusTarget: { window: Window; origin: string } | null = null;
// The last status posted. Consecutive duplicates collapse, so re-runs of the
// same terminal state (React StrictMode re-mounting effects, renderers
// re-rendering into the state they're already in) post once — hosts never
// need to debounce. State *changes* still flow, e.g. a handshake-timeout
// `error` followed by `loaded` when a late credential recovers the viewer. Kept as
// the full message (not just a dedup key) so a valid `auth` can replay it —
// see the replay note in the auth handler.
let lastStatus: ViewerStatusMessage | null = null;

function postStatus(message: ViewerStatusMessage): void {
  if (!statusTarget) return;
  if (lastStatus && JSON.stringify(message) === JSON.stringify(lastStatus)) {
    return;
  }
  lastStatus = message;
  statusTarget.window.postMessage(message, statusTarget.origin);
}

/** Report to the embedding host that the document rendered. */
export function notifyEmbedLoaded(): void {
  postStatus({ source: EMBED_MESSAGE_SOURCE, type: "loaded" });
}

/**
 * Report a terminal error state to the embedding host — the kind, plus the
 * raw HTTP status when there is one.
 */
export function notifyEmbedError({ kind, status }: ViewerError): void {
  postStatus(
    status === undefined
      ? { source: EMBED_MESSAGE_SOURCE, type: "error", kind }
      : { source: EMBED_MESSAGE_SOURCE, type: "error", kind, status },
  );
}

/**
 * Renderer seam for the success signal: posts `loaded` when `loaded` flips
 * true. Callers pass their own "content is rendering" condition, mirroring
 * the guards above their success return.
 */
export function useNotifyEmbedLoaded(loaded: boolean): void {
  useEffect(() => {
    if (loaded) notifyEmbedLoaded();
  }, [loaded]);
}

// The host version is advisory analytics metadata, so it must never break
// auth or requests: only a short header-safe token survives (an illegal
// character would make `Headers.set` throw on every cortex call), anything
// else is dropped while the credential still lands.
const HOST_VERSION_PATTERN = /^[\w.\-/@+]{1,64}$/;

function normalizeHostVersion(value: unknown): string | undefined {
  return typeof value === "string" && HOST_VERSION_PATTERN.test(value)
    ? value
    : undefined;
}

// A credential field only counts when it's a non-empty string — anything else
// (absent, "", a non-string) is treated as not sent.
function normalizeCredential(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function isAuthMessage(data: unknown): data is AuthMessage {
  return (
    !!data &&
    typeof data === "object" &&
    (data as AuthMessage).source === EMBED_MESSAGE_SOURCE &&
    (data as AuthMessage).type === "auth" &&
    (normalizeCredential((data as AuthMessage).apiKey) !== undefined ||
      normalizeCredential((data as AuthMessage).authToken) !== undefined)
  );
}

// The shell gives up waiting for `auth` after this long and shows the
// handshake-timeout error state.
export const HANDSHAKE_TIMEOUT_MS = 6_000;

// Bounded retry so a `ready` dropped by the host (its listener not yet
// attached, or its credential not yet set) is re-sent rather than lost. Stops
// as soon as the host replies with `auth`. The retries span the full
// handshake window: a stateless host that only answers `ready` when it holds
// a credential gets a fresh chance every interval until the shell gives up —
// stopping earlier would leave credential-arrives-late handshakes permanently
// dead.
const READY_RETRY_INTERVAL_MS = 300;
const READY_MAX_ATTEMPTS = HANDSHAKE_TIMEOUT_MS / READY_RETRY_INTERVAL_MS;

/**
 * Resolve the framing host's origin for the outbound `ready`. Prefers
 * `ancestorOrigins` (the exact immediate-parent origin, on Chrome/Safari);
 * falls back to `document.referrer` (the Firefox path). Returns "" when
 * neither is available — the handshake then fails closed (no `ready` is ever
 * posted to an unknown origin). Note this value is only ever used as a
 * `postMessage` target; the origin recorded for analytics comes from the
 * validated inbound `auth` event, never from the referrer.
 */
function resolveHostOrigin(): string {
  const ancestors = window.location.ancestorOrigins;
  if (ancestors && ancestors.length > 0) return ancestors[0];
  try {
    return document.referrer ? new URL(document.referrer).origin : "";
  } catch {
    return "";
  }
}

/**
 * Start the viewer side of the handshake. Returns a cleanup function. When the
 * host replies with a valid `auth`, the credential (auth token and/or legacy
 * API key) and the event's browser-asserted origin land in the auth store
 * (src/services/auth.ts). Later valid `auth` messages keep updating the store,
 * so a host may rotate the credential — a change to the effective credential
 * also refetches the page's queries under the new one (consumed SSO
 * exchanges excepted; see refetchOnCredentialChange).
 */
export function startEmbedHandshake(
  options: HandshakeOptions = {},
): () => void {
  const hostOrigin = options.hostOrigin ?? resolveHostOrigin();
  const parentWindow = options.parentWindow ?? window.parent;
  // Self-contained start: never inherit the status target or dedup state of a
  // previous handshake that wasn't cleaned up.
  statusTarget = null;
  lastStatus = null;
  let timer: ReturnType<typeof setInterval> | undefined;

  const stopRetry = () => {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };

  const onMessage = (event: MessageEvent) => {
    if (!isAllowedHostOrigin(event.origin, options)) return;
    if (event.source !== parentWindow) return;
    if (!isAuthMessage(event.data)) return;

    stopRetry();
    // The auth event's browser-asserted origin is the authoritative status
    // target from here on (the pre-auth hostOrigin came from ancestorOrigins
    // or the referrer).
    statusTarget = { window: parentWindow, origin: event.origin };
    // Replay the latest status: a pre-auth post (unknown-type fires within
    // milliseconds of boot) can race the host attaching its listener — the
    // very race the ready retry exists for — and would otherwise be lost
    // forever behind the dedup. `auth` is proof the host is listening now,
    // and a repeat of the current state is harmless to hosts that did get it.
    if (lastStatus) {
      statusTarget.window.postMessage(lastStatus, statusTarget.origin);
    }
    const previous = getEmbedAuth();
    const apiKey = normalizeCredential(event.data.apiKey);
    const authToken = normalizeCredential(event.data.authToken);
    setEmbedAuth({
      apiKey,
      authToken,
      parentOrigin: event.origin,
      hostVersion: normalizeHostVersion(event.data.hostVersion),
    });
    // Credential change (a refreshed token, or a rotated key): refetch the
    // page's queries under the new credential — the only refetch trigger in
    // the viewer (see refetchOnCredentialChange). Only on a change to the
    // *effective* credential, i.e. the one that goes on the wire (the token
    // when present, else the key — mirroring setCredentialHeader): hosts may
    // re-send the same auth, or drop a redundant legacy key while keeping the
    // same token, and neither must refetch-churn. A new credential within one
    // mounted viewer means the same principal; a host switching users must
    // remount the viewer instead (a fresh iframe is the session boundary),
    // since the viewer keeps already-fetched data on screen while it verifies
    // the new credential.
    if (
      previous !== null &&
      (previous.authToken ?? previous.apiKey) !== (authToken ?? apiKey)
    ) {
      void refetchOnCredentialChange();
    }
  };

  window.addEventListener("message", onMessage);

  const startReadyRetry = (targetOrigin: string) => {
    const ready: ReadyMessage = { source: EMBED_MESSAGE_SOURCE, type: "ready" };
    const postReady = () => parentWindow.postMessage(ready, targetOrigin);
    postReady();

    let attempts = 0;
    timer = setInterval(() => {
      attempts += 1;
      if (attempts >= READY_MAX_ATTEMPTS) {
        stopRetry();
        return;
      }
      postReady();
    }, READY_RETRY_INTERVAL_MS);
  };

  if (hostOrigin && isAllowedHostOrigin(hostOrigin, options)) {
    // Pre-auth status target, so failures *before* auth (handshake-timeout,
    // unknown-type) still reach the host.
    statusTarget = { window: parentWindow, origin: hostOrigin };
    startReadyRetry(hostOrigin);
  } else if (hostOrigin) {
    // A framing host we refuse to talk to. An empty hostOrigin is not an
    // error: it means top-level (no framing host at all) or an unresolvable
    // parent origin — the handshake fails closed (no `ready` is posted) and
    // the shell times out into its handshake state. Note the unresolvable
    // case includes Firefox (no `ancestorOrigins`) with a suppressed referrer.
    console.error("Embed handshake: framing host origin not allowlisted", {
      hostOrigin,
    });
  }

  return () => {
    window.removeEventListener("message", onMessage);
    stopRetry();
    statusTarget = null;
    lastStatus = null;
  };
}
